import { ButtonGroup } from "@blueprintjs/core";
import {
  FGCustomPanel,
  FormGenerator,
  IFormGeneratorProps,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import {
  CancelButton,
  DeleteButton,
  EditButton,
  FGRouteDirtyChecker,
  SaveButton,
} from ".";
import { useRouter, useTl } from "../hooks";
import { ETLCodes } from "../locales";

export interface CustomFormGeneratorProps<T>
  extends Omit<IFormGeneratorProps<T>, "children"> {
  loading?: boolean;
  onCancel?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
  saving?: boolean;
  editMode?: boolean;
  hideButtons?: boolean;
  editable?: boolean;
  cancelEdit?: boolean;
  showDeleteButton?: boolean;
  AdditionalButtons?: React.ReactElement;
}

const StyledForm = styled.div``;

export function CustomFormGenerator<T>(
  props: React.PropsWithChildren<CustomFormGeneratorProps<T>>
) {
  const {
    editable = true,
    children,
    loading = false,
    deleting,
    onDelete,
    onCancel,
    saving,
    editMode,
    minLabelWidth = 180,
    labelAlignment = "left",
    fill = true,
    showColons = false,
    inline = false,
    boldLabel = true,
    hideButtons = false,
    cancelEdit = false,
    showDeleteButton = true,
    AdditionalButtons,
    ...otherProps
  } = props;
  const { t, tUnsafe } = useTl();
  const { pathname, replace } = useRouter();
  const [dirtyMessage, setDirtyMessage] = React.useState(null);

  const changeEditMode = React.useCallback(
    (newEditMode: boolean) => {
      setDirtyMessage(t(ETLCodes.FormDirtyCancelMessage));
      setTimeout(() => {
        if (newEditMode) {
          replace(pathname.replace("view", "edit"), {});
        } else {
          replace(pathname.replace("edit", "view"), {});
        }
        setDirtyMessage(null);
      });
    },
    [pathname, replace, t]
  );

  return (
    <StyledForm>
      <FormGenerator
        editMode={editMode}
        minLabelWidth={minLabelWidth}
        labelAlignment={labelAlignment}
        fill={fill}
        inline={inline}
        boldLabel={boldLabel}
        showColons={showColons}
        loading={loading}
        translateFn={tUnsafe}
        {...otherProps}
      >
        <FGRouteDirtyChecker message={dirtyMessage} />
        {children}
        {!hideButtons && (
          <FGCustomPanel>
            {(ctx) => (
              <InlineButtonContainer>
                <ButtonGroup minimal={true}>
                  {onDelete && editMode && showDeleteButton && (
                    <DeleteButton
                      minimal={false}
                      loading={deleting}
                      onDelete={onDelete}
                    />
                  )}
                  {AdditionalButtons}
                </ButtonGroup>
                <ButtonGroup minimal={true}>
                  {!onCancel && editMode && cancelEdit && (
                    <CancelButton
                      minimal={false}
                      onClick={() => changeEditMode(false)}
                    />
                  )}
                  {onCancel && (
                    <CancelButton minimal={false} onClick={onCancel} />
                  )}
                  {editMode ? (
                    <SaveButton
                      minimal={false}
                      loading={saving}
                      disabled={
                        !ctx.formik.dirty && !(ctx.formik.submitCount > 0)
                      }
                    />
                  ) : (
                    editable && (
                      <EditButton
                        minimal={false}
                        onClick={() => changeEditMode(true)}
                      />
                    )
                  )}
                </ButtonGroup>
              </InlineButtonContainer>
            )}
          </FGCustomPanel>
        )}
      </FormGenerator>
    </StyledForm>
  );
}
